import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, where, orderBy, addDoc, updateDoc, deleteDoc, doc, Timestamp, writeBatch } from 'firebase/firestore';
import { db, functions, functionsUS } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { ProductCategory, Printer } from '../types';
import MediaUpload from '../components/MediaUpload';
import ImageUpload from '../components/ImageUpload';
import { useLanguage } from '../contexts/LanguageContext';
import { getLocalizedName } from '../utils/localization';

import { pctToRate } from '../utils/numberGuard';
const ProductCategories: React.FC = () => {
  const { shop } = useAuth();
  const { language } = useLanguage();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductCategory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    code: '',  // カテゴリコード（例: "001"）
    name: '',
    nameEn: '',
    nameTh: '',
    nameZhTw: '',
    nameKo: '',
    description: '',
    imageUrl: '',
    mediaType: 'image' as 'image' | 'video',
    displayStatus: 'available' as 'available' | 'hidden' | 'soldout',
    printerIds: [] as string[],
    serviceChargeEnabled: false,
    serviceChargeType: 'percentage' as 'percentage' | 'fixed',
    serviceChargeValue: 10,
    applyReducedTaxRate: false,
    // 売上分配設定（顧客要望③④⑤）
    distDeductCost: false,
    distProfitDeductionRate: 0, // パーセント (0-100)
    // 販売制限設定
    timeRestrictionEnabled: false,
    timeSlots: [{ start: '00:00', end: '23:59' }],
    dayOfWeekRestrictionEnabled: false,
    allowedDays: [] as number[],
    dateRestrictionEnabled: false,
    allowedDates: [] as string[],
    periodRestrictionEnabled: false,
    startDate: '',
    endDate: '',
    isActive: true,
    menuTitleCategoryId: '',
    staffOnly: false,
    planOnly: false,
  });

  // メニュータイトルカテゴリ管理用 state
  const [openTitleDialog, setOpenTitleDialog] = useState(false);
  const [editingTitleCategory, setEditingTitleCategory] = useState<any>(null);
  const [titleFormData, setTitleFormData] = useState({ name: '', nameEn: '', nameTh: '', nameZh: '', nameKo: '', imageUrl: '' });

  const menuTitleCategories = shop?.menuTitleCategories || [];

  const handleOpenTitleDialog = (titleCat?: any) => {
    if (titleCat) {
      setEditingTitleCategory(titleCat);
      setTitleFormData({ name: titleCat.name, nameEn: titleCat.nameEn || '', nameTh: titleCat.nameTh || '', nameZh: titleCat.nameZh || '', nameKo: titleCat.nameKo || '', imageUrl: titleCat.imageUrl || '' });
    } else {
      setEditingTitleCategory(null);
      setTitleFormData({ name: '', nameEn: '', nameTh: '', nameZh: '', nameKo: '', imageUrl: '' });
    }
    setOpenTitleDialog(true);
  };

  const handleSaveTitleCategory = async () => {
    if (!shop || !titleFormData.name.trim()) {
      setError('タイトル名は必須です');
      return;
    }
    try {
      const current = shop.menuTitleCategories || [];
      let updated;
      const saveData = { ...titleFormData, imageUrl: titleFormData.imageUrl || null };
      if (editingTitleCategory) {
        updated = current.map((tc: any) => tc.id === editingTitleCategory.id ? { ...tc, ...saveData } : tc);
      } else {
        const newId = `mtc_${Date.now()}`;
        const maxSort = current.length > 0 ? Math.max(...current.map((tc: any) => tc.sortOrder)) : 0;
        updated = [...current, { id: newId, ...saveData, sortOrder: maxSort + 1 }];
      }
      await updateDoc(doc(db, 'shops', shop.id), { menuTitleCategories: updated });
      setSuccess(editingTitleCategory ? 'タイトルカテゴリを更新しました' : 'タイトルカテゴリを追加しました');
      setOpenTitleDialog(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('タイトルカテゴリ保存エラー:', err);
      setError('保存に失敗しました');
    }
  };

  const handleMoveTitleCategorySortOrder = async (titleCat: any, direction: 'up' | 'down') => {
    if (!shop) return;
    const sorted = [...(shop.menuTitleCategories || [])].sort((a: any, b: any) => a.sortOrder - b.sortOrder);
    const currentIndex = sorted.findIndex((tc: any) => tc.id === titleCat.id);
    if ((direction === 'up' && currentIndex === 0) || (direction === 'down' && currentIndex === sorted.length - 1)) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const target = sorted[targetIndex];

    // sortOrderを入れ替え
    const updated = (shop.menuTitleCategories || []).map((tc: any) => {
      if (tc.id === titleCat.id) return { ...tc, sortOrder: target.sortOrder };
      if (tc.id === target.id) return { ...tc, sortOrder: titleCat.sortOrder };
      return tc;
    });

    try {
      await updateDoc(doc(db, 'shops', shop.id), { menuTitleCategories: updated });
    } catch (err) {
      console.error('並び順変更エラー:', err);
      setError('並び順の変更に失敗しました');
    }
  };

  const handleDeleteTitleCategory = async (titleCatId: string) => {
    if (!shop) return;
    if (!window.confirm('このタイトルカテゴリを削除しますか？所属するカテゴリは「未設定」になります。')) return;
    try {
      const updated = (shop.menuTitleCategories || []).filter((tc: any) => tc.id !== titleCatId);
      await updateDoc(doc(db, 'shops', shop.id), { menuTitleCategories: updated });
      // 所属カテゴリの menuTitleCategoryId をクリア
      const affectedCats = categories.filter(c => c.menuTitleCategoryId === titleCatId);
      for (const cat of affectedCats) {
        await updateDoc(doc(db, 'productCategories', cat.id), { menuTitleCategoryId: null });
      }
      setSuccess('タイトルカテゴリを削除しました');
      fetchCategories();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('タイトルカテゴリ削除エラー:', err);
      setError('削除に失敗しました');
    }
  };

  useEffect(() => {
    if (shop) {
      fetchCategories();
      fetchPrinters();
    }
  }, [shop]);

  const fetchCategories = async () => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'productCategories'),
        where('shopId', '==', shop.id)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as ProductCategory[];
      // クライアント側でソート
      data.sort((a, b) => a.sortOrder - b.sortOrder);
      setCategories(data);
    } catch (error) {
      console.error('カテゴリ取得エラー:', error);
      setError('カテゴリの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrinters = async () => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'printers'),
        where('shopId', '==', shop.id),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Printer[];
      // クライアント側でソート
      data.sort((a, b) => a.sortOrder - b.sortOrder);
      setPrinters(data);
    } catch (error) {
      console.error('プリンター取得エラー:', error);
    }
  };

  const handleOpenDialog = (category?: ProductCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        code: category.code || '',
        name: category.name,
        nameEn: category.nameEn || '',
        nameTh: category.nameTh || '',
        nameZhTw: category.nameZh || category.nameZhTw || '',  // nameZh優先（旧データ互換）
        nameKo: category.nameKo || '',
        description: category.description || '',
        imageUrl: category.imageUrl || '',
        mediaType: category.mediaType || 'image',
        displayStatus: category.displayStatus || 'available',
        printerIds: category.printerIds || [],
        serviceChargeEnabled: category.serviceCharge?.enabled || false,
        serviceChargeType: category.serviceCharge?.type || 'percentage',
        serviceChargeValue: category.serviceCharge?.type === 'percentage'
          ? (category.serviceCharge?.value || 0) * 100
          : (category.serviceCharge?.value || 0),
        applyReducedTaxRate: category.applyReducedTaxRate || false,
        distDeductCost: category.salesDistribution?.deductCost || false,
        distProfitDeductionRate: ((category.salesDistribution?.profitDeductionRate || 0) * 100),
        // 販売制限設定の読み込み
        timeRestrictionEnabled: category.salesRestrictions?.timeRestriction?.enabled || false,
        timeSlots: category.salesRestrictions?.timeRestriction?.timeSlots || [{ start: '00:00', end: '23:59' }],
        dayOfWeekRestrictionEnabled: category.salesRestrictions?.dayOfWeekRestriction?.enabled || false,
        allowedDays: category.salesRestrictions?.dayOfWeekRestriction?.allowedDays || [],
        dateRestrictionEnabled: category.salesRestrictions?.dateRestriction?.enabled || false,
        allowedDates: category.salesRestrictions?.dateRestriction?.allowedDates || [],
        periodRestrictionEnabled: category.salesRestrictions?.periodRestriction?.enabled || false,
        startDate: category.salesRestrictions?.periodRestriction?.startDate || '',
        endDate: category.salesRestrictions?.periodRestriction?.endDate || '',
        isActive: category.isActive,
        menuTitleCategoryId: category.menuTitleCategoryId || '',
        staffOnly: category.staffOnly || false,
        planOnly: category.planOnly || false,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        code: '',
        name: '',
        nameEn: '',
        nameTh: '',
        nameZhTw: '',
        nameKo: '',
        description: '',
        imageUrl: '',
        mediaType: 'image',
        displayStatus: 'available',
        printerIds: [],
        serviceChargeEnabled: false,
        serviceChargeType: 'percentage',
        serviceChargeValue: 10,
        applyReducedTaxRate: false,
        distDeductCost: false,
        distProfitDeductionRate: 0,
        // 販売制限設定のデフォルト
        timeRestrictionEnabled: false,
        timeSlots: [{ start: '00:00', end: '23:59' }],
        dayOfWeekRestrictionEnabled: false,
        allowedDays: [],
        dateRestrictionEnabled: false,
        allowedDates: [],
        periodRestrictionEnabled: false,
        startDate: '',
        endDate: '',
        isActive: true,
        menuTitleCategoryId: '',
        staffOnly: false,
        planOnly: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setError(null);
  };

  // カテゴリコードの自動採番
  const generateCategoryCode = (): string => {
    const codes = categories
      .map(c => c.code)
      .filter(code => code && /^\d+$/.test(code))
      .map(code => parseInt(code!, 10));

    const maxCode = codes.length > 0 ? Math.max(...codes) : 0;
    return String(maxCode + 1).padStart(2, '0');
  };

  const handleSubmit = async () => {
    if (!shop || !formData.name) {
      setError('カテゴリ名（日本語）は必須です');
      return;
    }

    // 利益控除率は 0〜100%。
    // inputProps の max は付いているが、保存ボタンが form の submit ではないため
    // HTML5 の検証が走らず、200 がそのまま通っていた。
    // 保存側で /100 するので profitDeductionRate: 2 になり、
    // backCalculation.ts:280 の `(1 - effRate)` が -1、
    // Math.max(0, 負) で 0 になる。
    // そのカテゴリの売上が全キャストのバック計算で丸ごと 0 になり、
    // エラーは一切出ない。
    const dedRate = Number(formData.distProfitDeductionRate);
    if (!Number.isFinite(dedRate) || dedRate < 0 || dedRate > 100) {
      setError('利益控除率は0〜100の範囲で入れてください');
      return;
    }

    try {
      const now = Timestamp.now();

      // カテゴリコード：空の場合は自動採番
      const categoryCode = formData.code.trim() || generateCategoryCode();

      const serviceCharge = formData.serviceChargeEnabled
        ? {
            enabled: true,
            type: formData.serviceChargeType,
            value: formData.serviceChargeType === 'percentage'
              ? formData.serviceChargeValue / 100
              : formData.serviceChargeValue,
          }
        : { enabled: false, type: 'percentage' as const, value: 0 };

      // 販売制限設定の作成
      const salesRestrictions = {
        timeRestriction: {
          enabled: formData.timeRestrictionEnabled,
          timeSlots: formData.timeRestrictionEnabled ? formData.timeSlots : [],
        },
        dayOfWeekRestriction: {
          enabled: formData.dayOfWeekRestrictionEnabled,
          allowedDays: formData.dayOfWeekRestrictionEnabled ? formData.allowedDays : [],
        },
        dateRestriction: {
          enabled: formData.dateRestrictionEnabled,
          allowedDates: formData.dateRestrictionEnabled ? formData.allowedDates : [],
        },
        periodRestriction: {
          enabled: formData.periodRestrictionEnabled,
          startDate: formData.periodRestrictionEnabled ? formData.startDate : '',
          endDate: formData.periodRestrictionEnabled ? formData.endDate : '',
        },
      };

      // 売上分配設定（顧客要望③④⑤）
      const salesDistribution = {
        deductCost: formData.distDeductCost,
        profitDeductionRate: pctToRate(dedRate),
      };

      if (editingCategory) {
        // 更新
        await updateDoc(doc(db, 'productCategories', editingCategory.id), {
          code: categoryCode,
          name: formData.name,
          nameEn: formData.nameEn || null,
          nameTh: formData.nameTh || null,
          nameZh: formData.nameZhTw || null,  // nameZhで統一
          nameKo: formData.nameKo || null,
          description: formData.description || null,
          imageUrl: formData.imageUrl || null,
          mediaType: formData.mediaType || null,
          displayStatus: formData.displayStatus,
          printerIds: formData.printerIds,
          serviceCharge: serviceCharge,
          applyReducedTaxRate: formData.applyReducedTaxRate,
          salesDistribution: salesDistribution,
          salesRestrictions: salesRestrictions,
          menuTitleCategoryId: formData.menuTitleCategoryId || null,
          isActive: formData.isActive,
          staffOnly: formData.staffOnly,
          planOnly: formData.planOnly,
          updatedAt: now,
        });
        setSuccess('カテゴリを更新しました');
      } else {
        // 新規作成
        const maxSortOrder = categories.length > 0
          ? Math.max(...categories.map(c => c.sortOrder))
          : 0;

        await addDoc(collection(db, 'productCategories'), {
          shopId: shop.id,
          code: categoryCode,
          name: formData.name,
          nameEn: formData.nameEn || null,
          nameTh: formData.nameTh || null,
          nameZh: formData.nameZhTw || null,  // nameZhで統一
          nameKo: formData.nameKo || null,
          description: formData.description || null,
          imageUrl: formData.imageUrl || null,
          mediaType: formData.mediaType || null,
          displayStatus: formData.displayStatus,
          printerIds: formData.printerIds,
          serviceCharge: serviceCharge,
          applyReducedTaxRate: formData.applyReducedTaxRate,
          salesDistribution: salesDistribution,
          salesRestrictions: salesRestrictions,
          sortOrder: maxSortOrder + 1,
          menuTitleCategoryId: formData.menuTitleCategoryId || null,
          isActive: formData.isActive,
          staffOnly: formData.staffOnly,
          planOnly: formData.planOnly,
          createdAt: now,
          updatedAt: now,
        });
        setSuccess('カテゴリを追加しました');
      }

      handleCloseDialog();
      fetchCategories();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('カテゴリ保存エラー:', error);
      setError('カテゴリの保存に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteDoc(doc(db, 'productCategories', deleteTarget.id));
      setSuccess('カテゴリを削除しました');
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
      fetchCategories();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('カテゴリ削除エラー:', error);
      setError('カテゴリの削除に失敗しました');
    }
  };

  /**
   * 全カテゴリを一括翻訳
   */
  const handleBatchTranslate = async () => {
    if (!shop || categories.length === 0) return;

    const confirmed = window.confirm(
      `全${categories.length}件のカテゴリを一括翻訳しますか？\n` +
      `※既に翻訳済みの項目はスキップされます\n` +
      `※処理には数分かかる場合があります`
    );

    if (!confirmed) return;

    setTranslating(true);
    setError(null);
    setTranslationProgress('翻訳準備中...');

    try {
      // タイムアウトを10分に設定
      const translateFn = httpsCallable(functions, 'translateMenuItems', { timeout: 600000 });

      // 元言語を決定（ユーザーの現在の言語）
      const sourceLanguage = language || 'ja';

      // 翻訳先言語（元言語以外の全言語）
      const allLanguages: ('ja' | 'en' | 'th' | 'zh-TW' | 'ko')[] = ['ja', 'en', 'th', 'zh-TW', 'ko'];
      const targetLanguages = allLanguages.filter(lang => lang !== sourceLanguage);

      // 翻訳対象データの作成
      const items = categories.map(cat => ({
        type: 'category' as const,
        id: cat.id,
        name: cat.name,
        nameEn: cat.nameEn,
        nameTh: cat.nameTh,
        nameZhTw: cat.nameZhTw,
        nameKo: cat.nameKo,
      }));

      setTranslationProgress(`${categories.length}件の翻訳を開始...`);

      // Claude APIで翻訳
      const result = await translateFn({
        shopName: shop.shopName,
        businessTypeCode: shop.businessTypeCode,
        sourceLanguage,
        targetLanguages,
        items,
        batchSize: 20,
        forceRetranslate: true,
      });

      const data = result.data as { translations: any[]; skipped: number };

      setTranslationProgress(`翻訳完了。Firestoreに保存中...`);

      // Firestoreに一括保存（Batch Write）
      const batch = writeBatch(db);
      let updateCount = 0;

      data.translations.forEach((translation: any) => {
        const categoryRef = doc(db, 'productCategories', translation.id);
        const updateData: any = {
          updatedAt: Timestamp.now(),
        };

        // 注意: nameは元言語なので上書きしない
        // 翻訳先の言語フィールドのみ更新
        if (translation.nameEn) updateData.nameEn = translation.nameEn;
        if (translation.nameTh) updateData.nameTh = translation.nameTh;
        if (translation.nameZhTw) updateData.nameZhTw = translation.nameZhTw;
        if (translation.nameKo) updateData.nameKo = translation.nameKo;
        if (translation.nameJa) updateData.nameJa = translation.nameJa;

        batch.update(categoryRef, updateData);
        updateCount++;
      });

      await batch.commit();

      setSuccess(
        `翻訳完了: ${updateCount}件を更新しました` +
        (data.skipped > 0 ? ` (${data.skipped}件は既に翻訳済みのためスキップ)` : '')
      );

      // データを再取得
      await fetchCategories();

      setTimeout(() => {
        setSuccess(null);
        setTranslationProgress('');
      }, 5000);
    } catch (error: any) {
      console.error('一括翻訳エラー:', error);
      setError(`翻訳に失敗しました: ${error.message || '不明なエラー'}`);
      setTranslationProgress('');
    } finally {
      setTranslating(false);
    }
  };

  const handleMoveSortOrder = async (category: ProductCategory, direction: 'up' | 'down') => {
    const currentIndex = categories.findIndex(c => c.id === category.id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === categories.length - 1)
    ) {
      return;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const targetCategory = categories[targetIndex];

    try {
      // 順序を入れ替え
      await updateDoc(doc(db, 'productCategories', category.id), {
        sortOrder: targetCategory.sortOrder,
        updatedAt: Timestamp.now(),
      });
      await updateDoc(doc(db, 'productCategories', targetCategory.id), {
        sortOrder: category.sortOrder,
        updatedAt: Timestamp.now(),
      });

      fetchCategories();
    } catch (error) {
      console.error('順序変更エラー:', error);
      setError('順序の変更に失敗しました');
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">商品カテゴリ管理</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={translating ? <span>⏳</span> : <TranslateIcon />}
            onClick={handleBatchTranslate}
            disabled={translating || categories.length === 0}
          >
            {translating ? translationProgress : '一括翻訳 (AI)'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            カテゴリ追加
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* メニュータイトルカテゴリ管理 */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">メニュータイトルカテゴリ</Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => handleOpenTitleDialog()}>
            追加
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          顧客ページでカテゴリをグループ分けするセクション名です（例: 飲み放題、フード、コース）。使わない店舗は未設定のままでOKです。
        </Typography>
        {menuTitleCategories.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...menuTitleCategories].sort((a: any, b: any) => a.sortOrder - b.sortOrder).map((tc: any, index: number, sorted: any[]) => {
              const count = categories.filter(c => c.menuTitleCategoryId === tc.id).length;
              return (
                <Box
                  key={tc.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  {/* 並び順ボタン */}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveTitleCategorySortOrder(tc, 'up')}
                      disabled={index === 0}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveTitleCategorySortOrder(tc, 'down')}
                      disabled={index === sorted.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* 画像サムネイル */}
                  {tc.imageUrl ? (
                    <Box
                      component="img"
                      src={tc.imageUrl}
                      alt={tc.name}
                      sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="caption" color="text.disabled">No img</Typography>
                    </Box>
                  )}

                  {/* 名前とカテゴリ数 */}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight="bold" noWrap>{tc.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{count}カテゴリ</Typography>
                  </Box>

                  {/* 操作ボタン */}
                  <IconButton size="small" onClick={() => handleOpenTitleDialog(tc)} color="primary" title="編集">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteTitleCategory(tc.id)} color="error" title="削除">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled">タイトルカテゴリが未登録です</Typography>
        )}
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>順序</TableCell>
                <TableCell>コード</TableCell>
                <TableCell>カテゴリ名</TableCell>
                <TableCell>タイトルカテゴリ</TableCell>
                {shop?.displaySettings?.showCategoryImages !== false && (
                  <TableCell>画像</TableCell>
                )}
                <TableCell>ステータス</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    カテゴリが登録されていません
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category, index) => (
                  <TableRow key={category.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveSortOrder(category, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveSortOrder(category, 'down')}
                          disabled={index === categories.length - 1}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', color: category.code ? 'text.primary' : 'text.disabled' }}>
                      {category.code || '-'}
                    </TableCell>
                    <TableCell>{getLocalizedName(category, language)}</TableCell>
                    <TableCell>
                      {category.menuTitleCategoryId ? (
                        <Chip
                          label={menuTitleCategories.find((tc: any) => tc.id === category.menuTitleCategoryId)?.name || '-'}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                    {shop?.displaySettings?.showCategoryImages !== false && (
                      <TableCell>
                        {category.imageUrl ? (
                          category.mediaType === 'video' ? (
                            <Box
                              component="video"
                              src={category.imageUrl}
                              sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1 }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={category.imageUrl}
                              alt={getLocalizedName(category, language)}
                              sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1 }}
                            />
                          )
                        ) : (
                          <Box
                            component="img"
                            src="/assets/no-photo.svg"
                            alt="画像なし"
                            sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1, opacity: 0.5 }}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={
                            // displayStatus を持たないカテゴリがある。
                            // 未設定を「非表示」に落とすと、実際には客側にも
                            // 代理注文にも出ているのに一覧だけ全件「非表示」になる。
                            // 編集ダイアログは「表示中」と出るので、表示が食い違う。
                            category.displayStatus === 'soldout' ? '売り切れ' :
                            category.displayStatus === 'hidden' ? '非表示' :
                            category.isActive === false ? '非表示' :
                            '表示中'
                          }
                          color={
                            category.displayStatus === 'soldout' ? 'warning' :
                            (category.displayStatus === 'hidden' || category.isActive === false) ? 'default' :
                            'success'
                          }
                          size="small"
                        />
                        {!category.isActive && (
                          <Chip
                            label="無効"
                            color="default"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {category.staffOnly && (
                          <Chip
                            label="スタッフ専用"
                            color="info"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {category.planOnly && (
                          <Chip
                            label="プラン専用"
                            color="secondary"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(category)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeleteTarget(category);
                          setOpenDeleteDialog(true);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 編集/追加ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'カテゴリ編集' : 'カテゴリ追加'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="カテゴリコード"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder={generateCategoryCode()}
              helperText="空欄なら自動採番"
              fullWidth
              inputProps={{ maxLength: 10 }}
            />
            <TextField
              label="カテゴリ名"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>メニュータイトルカテゴリ</InputLabel>
              <Select
                value={formData.menuTitleCategoryId}
                label="メニュータイトルカテゴリ"
                onChange={(e) => setFormData({ ...formData, menuTitleCategoryId: e.target.value })}
              >
                <MenuItem value="">未設定</MenuItem>
                {menuTitleCategories.map((tc: any) => (
                  <MenuItem key={tc.id} value={tc.id}>{tc.name}</MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                顧客ページでカテゴリをグループ分けする際のセクション名になります
              </Typography>
            </FormControl>
            <TextField
              label="説明"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="カテゴリ名 (English)"
              value={formData.nameEn}
              onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
              fullWidth
            />
            <TextField
              label="カテゴリ名 (ภาษาไทย)"
              value={formData.nameTh}
              onChange={(e) => setFormData({ ...formData, nameTh: e.target.value })}
              fullWidth
            />
            <TextField
              label="カテゴリ名 (繁體中文)"
              value={formData.nameZhTw}
              onChange={(e) => setFormData({ ...formData, nameZhTw: e.target.value })}
              fullWidth
            />
            <TextField
              label="カテゴリ名 (한국어)"
              value={formData.nameKo}
              onChange={(e) => setFormData({ ...formData, nameKo: e.target.value })}
              fullWidth
            />

            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.serviceChargeEnabled}
                    onChange={(e) => setFormData({ ...formData, serviceChargeEnabled: e.target.checked })}
                  />
                }
                label="サービスチャージを適用"
              />
              {formData.serviceChargeEnabled && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>タイプ</InputLabel>
                    <Select
                      value={formData.serviceChargeType}
                      label="タイプ"
                      onChange={(e) => setFormData({ ...formData, serviceChargeType: e.target.value as 'percentage' | 'fixed' })}
                    >
                      <MenuItem value="percentage">パーセント</MenuItem>
                      <MenuItem value="fixed">固定額</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label={formData.serviceChargeType === 'percentage' ? '割合 (%)' : '金額'}
                    type="number"
                    value={formData.serviceChargeValue}
                    onChange={(e) => setFormData({ ...formData, serviceChargeValue: Number(e.target.value) })}
                    inputProps={{ min: 0, step: formData.serviceChargeType === 'percentage' ? 0.1 : 1 }}
                    sx={{ flex: 1 }}
                  />
                </Box>
              )}
            </Box>

            {/* 売上分配設定（顧客要望③④⑤） */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                売上分配設定（スタッフインセンティブ）
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.distDeductCost}
                    onChange={(e) => setFormData({ ...formData, distDeductCost: e.target.checked })}
                  />
                }
                label="原価を控除する（シャンパン等）"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
                ON: 小計 = (店売価 − 原価) × (1 − 利益控除率) <br />
                OFF: 小計 = 店売価 × (1 − 利益控除率)
              </Typography>
              <TextField
                label="利益控除率 (%)"
                type="number"
                value={formData.distProfitDeductionRate}
                onChange={(e) => setFormData({ ...formData, distProfitDeductionRate: Number(e.target.value) })}
                inputProps={{ min: 0, max: 100, step: 1 }}
                size="small"
                helperText="例: 10 = 10% を分配対象から控除（顧客要望のデフォルト）"
                sx={{ mt: 1, width: 220 }}
              />
            </Box>

            {/* 軽減税率適用 */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.applyReducedTaxRate}
                    onChange={(e) => setFormData({ ...formData, applyReducedTaxRate: e.target.checked })}
                  />
                }
                label="軽減税率を適用"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                このカテゴリの商品に軽減税率（店舗設定で設定した税率）を適用します。<br />
                例: テイクアウト、持ち帰り用の商品など
              </Typography>
            </Box>

            {/* スタッフ専用カテゴリ */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.staffOnly}
                    onChange={(e) => setFormData({ ...formData, staffOnly: e.target.checked })}
                  />
                }
                label="スタッフ専用カテゴリ"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                ONにすると、モバイルオーダー画面（お客様向け）には表示されず、<br />
                スタッフアプリやダッシュボードからのみ注文できます。<br />
                例: お通し、セットメニュー、スタッフドリンク等
              </Typography>
            </Box>

            {/* プラン専用カテゴリ */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.planOnly}
                    onChange={(e) => setFormData({ ...formData, planOnly: e.target.checked })}
                  />
                }
                label="プラン専用カテゴリ"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                ONにすると、飲み放題・食べ放題プランが有効な場合のみ<br />
                お客様のモバイルオーダー画面に表示されます。<br />
                例: 飲み放題限定カクテル、コース専用デザート等
              </Typography>
            </Box>

            {/* 販売制限設定 */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>販売制限設定</Typography>

              {/* 販売時間制限 */}
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.timeRestrictionEnabled}
                    onChange={(e) => setFormData({ ...formData, timeRestrictionEnabled: e.target.checked })}
                  />
                }
                label="販売時間を制限"
              />
              {formData.timeRestrictionEnabled && (
                <Box sx={{ mt: 1, ml: 4 }}>
                  {formData.timeSlots.map((slot, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                      <TextField
                        label="開始時間"
                        type="time"
                        value={slot.start}
                        onChange={(e) => {
                          const newSlots = [...formData.timeSlots];
                          newSlots[index].start = e.target.value;
                          setFormData({ ...formData, timeSlots: newSlots });
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="終了時間"
                        type="time"
                        value={slot.end}
                        onChange={(e) => {
                          const newSlots = [...formData.timeSlots];
                          newSlots[index].end = e.target.value;
                          setFormData({ ...formData, timeSlots: newSlots });
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                      {formData.timeSlots.length > 1 && (
                        <IconButton
                          onClick={() => {
                            const newSlots = formData.timeSlots.filter((_, i) => i !== index);
                            setFormData({ ...formData, timeSlots: newSlots });
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  <Button
                    size="small"
                    onClick={() => setFormData({ ...formData, timeSlots: [...formData.timeSlots, { start: '00:00', end: '23:59' }] })}
                    sx={{ mt: 1 }}
                  >
                    時間帯を追加
                  </Button>
                </Box>
              )}

              {/* 販売曜日制限 */}
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.dayOfWeekRestrictionEnabled}
                    onChange={(e) => setFormData({ ...formData, dayOfWeekRestrictionEnabled: e.target.checked })}
                  />
                }
                label="販売曜日を制限"
                sx={{ mt: 2 }}
              />
              {formData.dayOfWeekRestrictionEnabled && (
                <Box sx={{ mt: 1, ml: 4, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['日', '月', '火', '水', '木', '金', '土'].map((day, index) => (
                    <Chip
                      key={index}
                      label={day}
                      onClick={() => {
                        const newDays = formData.allowedDays.includes(index)
                          ? formData.allowedDays.filter(d => d !== index)
                          : [...formData.allowedDays, index];
                        setFormData({ ...formData, allowedDays: newDays });
                      }}
                      color={formData.allowedDays.includes(index) ? 'primary' : 'default'}
                      variant={formData.allowedDays.includes(index) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              )}

              {/* 販売日制限 */}
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.dateRestrictionEnabled}
                    onChange={(e) => setFormData({ ...formData, dateRestrictionEnabled: e.target.checked })}
                  />
                }
                label="販売日を制限"
                sx={{ mt: 2 }}
              />
              {formData.dateRestrictionEnabled && (
                <Box sx={{ mt: 1, ml: 4 }}>
                  <TextField
                    label="販売日を追加"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const input = e.target as HTMLInputElement;
                        const date = input.value;
                        if (date && !formData.allowedDates.includes(date)) {
                          setFormData({ ...formData, allowedDates: [...formData.allowedDates, date] });
                          input.value = '';
                        }
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {formData.allowedDates.map((date) => (
                      <Chip
                        key={date}
                        label={date}
                        onDelete={() => {
                          setFormData({ ...formData, allowedDates: formData.allowedDates.filter(d => d !== date) });
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* 販売期間制限 */}
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.periodRestrictionEnabled}
                    onChange={(e) => setFormData({ ...formData, periodRestrictionEnabled: e.target.checked })}
                  />
                }
                label="販売期間を制限"
                sx={{ mt: 2 }}
              />
              {formData.periodRestrictionEnabled && (
                <Box sx={{ mt: 1, ml: 4, display: 'flex', gap: 2 }}>
                  <TextField
                    label="開始日"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="終了日"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              )}
            </Box>

            <MediaUpload
              currentMediaUrl={formData.imageUrl}
              currentMediaType={formData.mediaType}
              onMediaUploaded={(url, type) => setFormData({ ...formData, imageUrl: url, mediaType: type })}
              onMediaDeleted={() => setFormData({ ...formData, imageUrl: '', mediaType: 'image' })}
              storagePath={`product-categories/${editingCategory?.id || 'new'}`}
              label="カテゴリメディア"
            />

            <FormControl fullWidth>
              <InputLabel>プリンター（複数選択可）</InputLabel>
              <Select
                multiple
                value={formData.printerIds}
                label="プリンター（複数選択可）"
                onChange={(e) => setFormData({ ...formData, printerIds: e.target.value as string[] })}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const printer = printers.find(p => p.id === id);
                      return printer ? <Chip key={id} label={printer.name} size="small" /> : null;
                    })}
                  </Box>
                )}
              >
                {printers.map((printer) => (
                  <MenuItem key={printer.id} value={printer.id}>
                    {printer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>表示ステータス</Typography>

            <ToggleButtonGroup
              value={formData.displayStatus}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) {
                  setFormData({ ...formData, displayStatus: newValue });
                }
              }}
              fullWidth
            >
              <ToggleButton value="available" color="success">
                表示中
              </ToggleButton>
              <ToggleButton value="soldout" color="warning">
                売り切れ
              </ToggleButton>
              <ToggleButton value="hidden" color="standard">
                非表示
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCategory ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>カテゴリを削除</DialogTitle>
        <DialogContent>
          <Typography>
            カテゴリ「{deleteTarget?.name}」を削除してもよろしいですか？
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            ※ このカテゴリに属する商品も影響を受ける可能性があります。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>キャンセル</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* メニュータイトルカテゴリ編集ダイアログ */}
      <Dialog open={openTitleDialog} onClose={() => setOpenTitleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTitleCategory ? 'タイトルカテゴリ編集' : 'タイトルカテゴリ追加'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="タイトル名"
              value={titleFormData.name}
              onChange={(e) => setTitleFormData({ ...titleFormData, name: e.target.value })}
              required
              fullWidth
              placeholder="例: 飲み放題、フード、コース"
            />
            <TextField
              label="English"
              value={titleFormData.nameEn}
              onChange={(e) => setTitleFormData({ ...titleFormData, nameEn: e.target.value })}
              fullWidth
            />
            <TextField
              label="ภาษาไทย"
              value={titleFormData.nameTh}
              onChange={(e) => setTitleFormData({ ...titleFormData, nameTh: e.target.value })}
              fullWidth
            />
            <TextField
              label="繁體中文"
              value={titleFormData.nameZh}
              onChange={(e) => setTitleFormData({ ...titleFormData, nameZh: e.target.value })}
              fullWidth
            />
            <TextField
              label="한국어"
              value={titleFormData.nameKo}
              onChange={(e) => setTitleFormData({ ...titleFormData, nameKo: e.target.value })}
              fullWidth
            />
            <ImageUpload
              currentImageUrl={titleFormData.imageUrl || undefined}
              onImageUploaded={(url) => setTitleFormData({ ...titleFormData, imageUrl: url })}
              onImageDeleted={() => setTitleFormData({ ...titleFormData, imageUrl: '' })}
              storagePath={`menu-title-categories/${editingTitleCategory?.id || `new_${Date.now()}`}`}
              label="タイトルカテゴリ画像"
              maxSizeMB={5}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTitleDialog(false)}>キャンセル</Button>
          <Button onClick={handleSaveTitleCategory} variant="contained">
            {editingTitleCategory ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductCategories;
